import cloneDeep from "lodash.clonedeep";
import uniqBy from "lodash.uniqby";
import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ReadyState } from "react-use-websocket";
import { useImmer } from "use-immer";

import {
  GetProcessesResponseBodyType,
  ProcessStatusTableDto,
} from "@intelligems/ig-types";
import { useDiscountSyncErrorToast } from "@src/hooks/toast";
import { useQueryClient } from "@tanstack/react-query";

import { useGetOrg } from "../api/organizations/get-organization";
import { personalizationKeys } from "../api/personalizations/use-dashboard-personalization-or-default";
import {
  experienceKeys,
  experienceProductsKeys,
  historyKeys,
  organizationsKeys,
  processesKeys,
  scheduleKeys,
} from "../api/queryKeys";
import { useWebsocket } from "../hooks/websocket";
import {
  getIgnoreUpdateDetected,
  isDialogOpen,
  setDialog,
  setEventProgress,
} from "../store/slices/global";
import { useEditPageExperienceId, useIsEditPage } from "../utils/constants";

export const WebsocketProvider: FC = () => {
  const [productProgressAttempts, setProductProgressAttempts] =
    useImmer<number>(0);
  const organization = useGetOrg();
  const websocket = useWebsocket();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isEditPage = useIsEditPage();
  const editPageExperienceId = useEditPageExperienceId();
  const discountSyncErrorToast = useDiscountSyncErrorToast();
  const requestAllEventProgresses = () => {
    websocket.sendJsonMessage({ action: "requestAllEventProgresses" });
  };

  const ignoreUpdateDetected = useSelector(getIgnoreUpdateDetected);
  const isModifiedDialogOpen = useSelector(isDialogOpen("updatedDetected"));
  const isOpenPreviewDialogOpen = useSelector(
    isDialogOpen("redirectToPluginDialog")
  );

  useEffect(() => {
    if (websocket.readyState === ReadyState.OPEN) {
      requestAllEventProgresses();
    }
  }, [websocket.readyState]);

  useEffect(() => {
    if (!isEditPage && isModifiedDialogOpen && !isOpenPreviewDialogOpen) {
      dispatch(setDialog({ dialog: "updatedDetected", open: false }));
    }
  }, [isEditPage, isModifiedDialogOpen, isOpenPreviewDialogOpen]);

  useEffect(() => {
    let eventProgressTimeout: NodeJS.Timeout | undefined = undefined;
    if (websocket.lastJsonMessage) {
      if (websocket.lastJsonMessage.type === "eventProgress") {
        dispatch(setEventProgress(websocket.lastJsonMessage));
        if (websocket.lastJsonMessage.reason === "response") {
          eventProgressTimeout = setTimeout(() => {
            setProductProgressAttempts((draft) => {
              draft++;
              return draft;
            });
            if (productProgressAttempts < 20) {
              requestAllEventProgresses();
            }
          }, 5000);
          if (websocket.lastJsonMessage.progress >= 1) {
            clearTimeout(eventProgressTimeout);
          }
        }
      } else if (websocket.lastJsonMessage.type === "configUpdate") {
        void queryClient.invalidateQueries({
          queryKey: historyKeys.allConfigEvents,
        });
      } else if (websocket.lastJsonMessage.type === "invalidateOrgSummary") {
        void queryClient.invalidateQueries({
          queryKey: organizationsKeys.orgSummary(organization.data.org.id),
        });
        void queryClient.invalidateQueries({ queryKey: organizationsKeys.one });
      } else if (websocket.lastJsonMessage.type === "processUpdate") {
        const lastJsonMessage = websocket.lastJsonMessage;
        if (lastJsonMessage.action === "start") {
          queryClient.setQueryData<GetProcessesResponseBodyType>(
            processesKeys.one(organization.data.org.id),
            (data) => {
              return {
                processes: uniqBy(
                  [
                    lastJsonMessage.process as ProcessStatusTableDto,
                    ...(data?.processes || []),
                  ],
                  (_) => _.type
                ),
              };
            }
          );
        } else if (lastJsonMessage.action === "update") {
          queryClient.setQueryData<GetProcessesResponseBodyType>(
            processesKeys.one(organization.data.org.id),
            (data) => {
              if (data?.processes) {
                const index = (data?.processes || []).findIndex(
                  (process) => process.type === lastJsonMessage.process.type
                );
                if (index > -1) {
                  const processes: ProcessStatusTableDto[] = cloneDeep(
                    data.processes
                  );
                  if ("loadingPercent" in lastJsonMessage.process) {
                    if (
                      lastJsonMessage.process.loadingPercent &&
                      lastJsonMessage.process.loadingPercent >=
                        (processes[index].loadingPercent || 0)
                    ) {
                      processes[index] = lastJsonMessage.process;
                    }
                  } else {
                    processes[index] =
                      lastJsonMessage.process as ProcessStatusTableDto;
                  }
                  return {
                    processes,
                  };
                }
              }

              return {
                processes: uniqBy(data?.processes || [], (_) => _.type),
              };
            }
          );
        } else {
          queryClient.setQueryData<GetProcessesResponseBodyType>(
            processesKeys.one(organization.data.org.id),
            (data) => {
              if (
                data?.processes.some(
                  (p) =>
                    p.id === undefined &&
                    p.type === lastJsonMessage.process.type
                )
              ) {
                return {
                  processes:
                    data?.processes.filter(
                      (process) => process.type !== lastJsonMessage.process.type
                    ) || [],
                };
              }
              return {
                processes:
                  data?.processes.filter(
                    (process) => process.id !== lastJsonMessage.process.id
                  ) || [],
              };
            }
          );
        }
      } else if (websocket.lastJsonMessage.type === "entityModification") {
        if (
          isEditPage &&
          !isOpenPreviewDialogOpen &&
          !ignoreUpdateDetected &&
          !websocket.lastJsonMessage.testProducts
        ) {
          if (
            websocket.lastJsonMessage.experienceId &&
            websocket.lastJsonMessage.experienceId === editPageExperienceId
          ) {
            setTimeout(() => {
              dispatch(setDialog({ dialog: "updatedDetected", open: true }));
            }, 0);
          }
        } else {
          if (websocket.lastJsonMessage.experience) {
            queryClient.setQueryData(
              experienceKeys.one(websocket.lastJsonMessage.experience.id),
              { experience: websocket.lastJsonMessage.experience }
            );
            void queryClient.invalidateQueries({
              queryKey: scheduleKeys.one(
                websocket.lastJsonMessage.experience.id
              ),
            });
          } else if (websocket.lastJsonMessage.experienceId) {
            void queryClient.invalidateQueries({
              queryKey: experienceKeys.one(
                websocket.lastJsonMessage.experienceId
              ),
            });
            void queryClient.invalidateQueries({
              queryKey: scheduleKeys.one(
                websocket.lastJsonMessage.experienceId
              ),
            });
            if (websocket.lastJsonMessage.testProducts) {
              void queryClient.invalidateQueries({
                queryKey: experienceProductsKeys.one(
                  websocket.lastJsonMessage.experienceId
                ),
              });
            }
          } else if (websocket.lastJsonMessage.campaignId) {
            void queryClient.invalidateQueries({
              queryKey: personalizationKeys.one(
                websocket.lastJsonMessage.campaignId
              ),
            });
          }
        }
        if (websocket.lastJsonMessage.experienceId) {
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.experimentList,
          });
        } else if (websocket.lastJsonMessage.campaignId) {
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.personalizationList,
          });
          void queryClient.invalidateQueries({
            queryKey: personalizationKeys.all(true),
          });
          void queryClient.invalidateQueries({
            queryKey: personalizationKeys.all(false),
          });
        }
        if (websocket.lastJsonMessage.message) {
          toast.success(websocket.lastJsonMessage.message);
        }
      } else if (
        websocket.lastJsonMessage.type === "syncPricingFunctionComplete"
      ) {
        void queryClient.invalidateQueries({
          queryKey: organizationsKeys.cartTransforms(organization.data.org.id),
        });
        void queryClient.invalidateQueries({
          queryKey: organizationsKeys.metafield(organization.data.org.id),
        });
        queryClient.setQueryData<GetProcessesResponseBodyType>(
          processesKeys.one(organization.data.org.id),
          (data) => {
            return {
              processes:
                data?.processes.filter(
                  (process) => process.type !== "syncPricingFunction"
                ) || [],
            };
          }
        );
      } else if (
        websocket.lastJsonMessage.type === "syncDiscountFunctionStatusChange"
      ) {
        discountSyncErrorToast(websocket.lastJsonMessage.status);
      }
    }
    return () => {
      if (eventProgressTimeout) {
        clearTimeout(eventProgressTimeout);
      }
    };
  }, [websocket.lastJsonMessage, organization.data.org.id]);

  return null;
};
